import { FlexLayout, Icon } from '@deity/falcon-ui';
import type { IDefinitionItem } from '@deity-io/di-data-definitions';
import type { Flow } from '@deity-io/di-data-flows';
import { FlowLoader } from '@deity-io/di-data-flows';
import { Collapsible } from 'components/Collapsible';
import { getComponentDataSchema } from 'components/FlowEditor/helpers/flowHelpers';
import SchemaView from 'components/SchemaEditor/SchemaView';
import { CreateEditStructureModal } from 'components/Structures';
import { Tooltip } from 'components/Tooltip';
import { FlowContext } from 'contexts/FlowContext';
import { EDITOR_PATH_ID, ENTITY_PATH } from 'contracts/constants';
import { useContext, useState } from 'react';
import ReactJson from 'react-json-view';
import { useLocation } from 'react-router-dom';
import { getParams } from 'utils';

const PortSchema = ({ isEditable }) => {
  const ctx = useContext(FlowContext);
  const [structureModelState, setStructureModelState] = useState<{
    open: boolean;
    structure?: IDefinitionItem | Array<IDefinitionItem>;
  }>({
    open: false
  });

  const [editorView, setEditorView] = useState<{
    first: 'json' | 'structure';
    second: 'json' | 'structure';
  }>({
    first: 'json',
    second: 'json'
  });
  const { pathname } = useLocation();
  const params: any = getParams(pathname, [EDITOR_PATH_ID, ENTITY_PATH]);
  if (
    ctx?.state.flow &&
    ctx.state.flowSchema &&
    ctx.state.selectedComponent &&
    'port' in ctx.state.selectedComponent &&
    'componentId' in ctx.state.selectedComponent
  ) {
    let isExternalEntity = false;
    let flow: Flow = ctx?.state.flow;

    if (ctx.state.selectedComponent.flowId) {
      const getEntities = ctx.state.flow.getEntityCallback();
      if (getEntities) {
        const entity = getEntities('flow', ctx.state.selectedComponent.flowId);
        if (entity) {
          flow = FlowLoader.tryLoad('entity', entity)!;
          isEditable = false;
          isExternalEntity = true;
        } else {
          // eslint-disable-next-line no-console
          console.warn('failed to fetch entity');
        }
      }
    }

    const selectedComponent = ctx?.state.selectedComponent;
    const messages: Array<string> = [];
    const portInfo = getComponentDataSchema(flow, selectedComponent.componentId, selectedComponent.port, messages);
    let jsonData;
    const data = portInfo?.direction === 'input' ? portInfo.requirements : portInfo?.schema;
    if (data) {
      if (Array.isArray(data)) {
        jsonData = data.map(item => item.toJSON());
      } else {
        jsonData = data.toJSON();
      }
    }

    let targetData;
    let targetPort;
    let targetInfo;
    if ('targetPort' in selectedComponent && 'targetComponentId' in selectedComponent) {
      // eslint-disable-next-line prefer-destructuring
      targetPort = selectedComponent.targetPort;
      targetInfo = getComponentDataSchema(
        ctx?.state.flow!,
        selectedComponent.targetComponentId,
        selectedComponent.targetPort,
        messages
      );
      const requirements = targetInfo?.direction === 'input' ? targetInfo.requirements : targetInfo?.schema;
      if (requirements) {
        if (Array.isArray(requirements)) {
          targetData = requirements.map(item => item.toJSON());
        } else {
          targetData = requirements.toJSON();
        }
      }
    }
    if (messages.length) {
      // eslint-disable-next-line no-console
      console.error(messages);
    }

    return (
      <FlexLayout pr="lg" pl="xs" justifyContent="flex-start">
        <Collapsible
          collapsed={false}
          title={
            portInfo?.direction === 'input'
              ? `Requirements  on port : ${selectedComponent.port}`
              : `Data on port : ${selectedComponent.port}`
          }
          key="source"
        >
          <FlexLayout width="full" justifyContent="flex-end" cursor="grab">
            <Tooltip content="Switch view" direction="left">
              <Icon
                src="shuffle"
                onClick={() => {
                  setEditorView({ ...editorView, first: editorView.first === 'json' ? 'structure' : 'json' });
                }}
              />
            </Tooltip>
            <Tooltip content="Store schema" direction="left">
              <Icon
                src="add"
                onClick={() => {
                  setStructureModelState({
                    ...structureModelState,
                    open: true,
                    structure: portInfo?.direction === 'input' ? portInfo.requirements : portInfo?.schema
                  });
                }}
              />
            </Tooltip>
          </FlexLayout>
          {editorView.first === 'json' && (
            <ReactJson src={jsonData} name={null} theme="ashes" collapseStringsAfterLength={18} />
          )}
          {editorView.first === 'structure' && (
            <FlexLayout css={{ scale: 0.8 }}>
              <SchemaView
                field={portInfo?.direction === 'input' ? 'requirement' : 'schema'}
                // selectedField={actualSelectedField}
                // onSelect={setSelectedField}
                schema={jsonData}
              />
            </FlexLayout>
          )}
        </Collapsible>
        {targetPort && (
          <Collapsible collapsed={false} title={`Requirements on port : ${targetPort}`} key="target">
            {targetData && (
              <FlexLayout width="full" justifyContent="flex-end" cursor="grab">
                <Tooltip content="Switch view" direction="left">
                  <Icon
                    src="shuffle"
                    onClick={() => {
                      setEditorView({ ...editorView, second: editorView.second === 'json' ? 'structure' : 'json' });
                    }}
                  />
                </Tooltip>
                <Tooltip content="Store schema" direction="left">
                  <Icon
                    src="add"
                    onClick={() => {
                      setStructureModelState({
                        ...structureModelState,
                        open: true,
                        structure: targetInfo.requirements
                      });
                    }}
                  />
                </Tooltip>
              </FlexLayout>
            )}
            {targetData && editorView.second === 'json' && (
              <ReactJson src={targetData} name={null} theme="ashes" collapseStringsAfterLength={18} />
            )}
            {targetData && editorView.second === 'structure' && (
              <FlexLayout css={{ scale: 0.8 }}>
                <SchemaView
                  field="requirement"
                  // selectedField={actualSelectedField}
                  // onSelect={setSelectedField}
                  schema={targetData}
                />
              </FlexLayout>
            )}
            {!targetData && 'No Requirements'}
          </Collapsible>
        )}
        {structureModelState.open && (
          <CreateEditStructureModal
            key="CreateEditStructureModal"
            isOpen={structureModelState.open}
            versionUuid={params.versionUuid}
            setOpenState={isOpen => setStructureModelState({ ...structureModelState, open: isOpen })}
            schema={structureModelState.structure}
            onPackageCreated={() => {
              // eslint-disable-next-line no-console
              console.log('onPackageCreated');
            }}
          />
        )}
      </FlexLayout>
    );
  }

  return <FlexLayout />;
};

export default PortSchema;
